import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode, Ref } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Icon from './Icon';
import { mediumDate, todayISO } from '@/lib/dates';
import { SUPPORTED_CURRENCIES } from '@/lib/money';

// Shared primitives — the web counterparts of mobile/components/ui.tsx
// (ScreenHeader, SectionLabel, Card, ListRow, IconCircle, Tag, Button,
// SegmentedControl, ChipRow, TextField, Hint, MonthGrid). Styling lives
// in styles/app.css; these only assemble markup and a11y wiring.

function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

// ─── Buttons ─────────────────────────────────────────────────────────

type ButtonVariant = 'primary' | 'secondary' | 'destructive' | 'danger' | 'dark' | 'ghost';

export function Button({
  variant = 'primary',
  size = 'md',
  icon,
  block,
  to,
  children,
  onClick,
  disabled,
  type = 'button',
  className,
  ...aria
}: {
  variant?: ButtonVariant;
  size?: 'md' | 'sm';
  icon?: string;
  block?: boolean;
  /** Render as a router link instead of a button. */
  to?: string;
  children?: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  type?: 'button' | 'submit';
  className?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
}) {
  const cls = cx(
    'btn',
    `btn-${variant}`,
    size === 'sm' && 'btn-sm',
    block && 'btn-block',
    className,
  );
  const inner = (
    <>
      {icon && <Icon name={icon} size={size === 'sm' ? 16 : 18} stroke={2} />}
      {children}
    </>
  );
  if (to && !disabled) {
    return (
      <Link to={to} className={cls} {...aria}>
        {inner}
      </Link>
    );
  }
  return (
    <button type={type} className={cls} onClick={onClick} disabled={disabled} {...aria}>
      {inner}
    </button>
  );
}

// ─── Headers + layout ────────────────────────────────────────────────

/** Page title row. `back` renders a round back button to that path. */
export function PageHeader({
  title,
  subtitle,
  back,
  actions,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  back?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="page-head">
      {back && (
        <Link to={back} className="icon-btn" aria-label="Back">
          <Icon name="chevronLeft" size={22} />
        </Link>
      )}
      <div className="page-head-text">
        <h1 className="page-title">{title}</h1>
        {subtitle && <p className="page-sub">{subtitle}</p>}
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </div>
  );
}

export function Card({
  children,
  className,
  pad,
}: {
  children: ReactNode;
  className?: string;
  pad?: boolean;
}) {
  return <div className={cx('card', pad && 'card-pad', className)}>{children}</div>;
}

/** 13px semibold sentence-case label above a section. Renders a
 *  <label> when it names a single input (htmlFor), else a heading. */
export function SectionLabel({
  children,
  aside,
  first,
  htmlFor,
  id,
}: {
  children: ReactNode;
  aside?: ReactNode;
  first?: boolean;
  htmlFor?: string;
  id?: string;
}) {
  const cls = cx('section-label', first && 'is-first');
  if (htmlFor) {
    return (
      <label className={cls} htmlFor={htmlFor} id={id}>
        <span>{children}</span>
        {aside && <span className="section-aside">{aside}</span>}
      </label>
    );
  }
  return (
    <h2 className={cls} id={id}>
      <span>{children}</span>
      {aside && <span className="section-aside">{aside}</span>}
    </h2>
  );
}

// ─── Visual atoms ────────────────────────────────────────────────────

export type Tone = 'accent' | 'owed' | 'owe' | 'warn' | 'neutral';

/** Round badge holding an icon or a single letter. `tint` (0–7) picks a
 *  merchant avatar color and overrides `tone`. */
export function IconCircle({
  icon,
  letter,
  tone = 'neutral',
  tint,
  iconSize = 20,
}: {
  icon?: string;
  letter?: string;
  tone?: Tone;
  tint?: number;
  iconSize?: number;
}) {
  const cls = cx(
    'icon-circle',
    tint !== undefined ? `tint-${tint}` : tone !== 'neutral' && `tone-${tone}`,
  );
  return (
    <span className={cls} aria-hidden="true">
      {icon ? (
        <Icon name={icon} size={iconSize} stroke={1.8} />
      ) : (
        (letter ?? '?').trim().slice(0, 1).toUpperCase() || '?'
      )}
    </span>
  );
}

export function Tag({ children, tone = 'neutral' }: { children: ReactNode; tone?: Tone }) {
  return <span className={cx('tag', tone !== 'neutral' && `tone-${tone}`)}>{children}</span>;
}

// ─── Controls ────────────────────────────────────────────────────────

// Arrow-key movement inside a single-select group (radio semantics).
function radioKeys<K>(
  e: KeyboardEvent<HTMLElement>,
  keys: K[],
  value: K | null,
  onChange: (k: K) => void,
) {
  const i = value === null ? -1 : keys.indexOf(value);
  let next = -1;
  if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (i + 1) % keys.length;
  else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (i - 1 + keys.length) % keys.length;
  else return;
  e.preventDefault();
  onChange(keys[next]);
  const group = e.currentTarget;
  requestAnimationFrame(() => {
    const btn = group.querySelectorAll<HTMLButtonElement>('[role="radio"]')[next];
    btn?.focus();
  });
}

/** iOS-style segmented control: grey track, raised active segment. */
export function SegmentedControl<K extends string>({
  options,
  value,
  onChange,
  labelledBy,
  label,
}: {
  options: Array<{ key: K; label: string }>;
  value: K;
  onChange: (key: K) => void;
  labelledBy?: string;
  label?: string;
}) {
  const keys = options.map((o) => o.key);
  return (
    <div
      className="seg"
      role="radiogroup"
      aria-labelledby={labelledBy}
      aria-label={label}
      onKeyDown={(e) => radioKeys(e, keys, value, onChange)}
    >
      {options.map((o) => {
        const on = o.key === value;
        return (
          <button
            key={o.key}
            type="button"
            role="radio"
            aria-checked={on}
            tabIndex={on ? 0 : -1}
            className="seg-btn"
            onClick={() => onChange(o.key)}
            title={o.label}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** Wrapping row of single-select pill chips (people, methods). */
export function ChipRow<K extends string>({
  options,
  value,
  onChange,
  labelledBy,
  label,
}: {
  options: Array<{ key: K; label: string }>;
  value: K | null;
  onChange: (key: K) => void;
  labelledBy?: string;
  label?: string;
}) {
  const keys = options.map((o) => o.key);
  const focusable = value !== null && keys.includes(value) ? value : keys[0];
  return (
    <div
      className="chips"
      role="radiogroup"
      aria-labelledby={labelledBy}
      aria-label={label}
      onKeyDown={(e) => radioKeys(e, keys, value, onChange)}
    >
      {options.map((o) => {
        const on = o.key === value;
        return (
          <button
            key={o.key}
            type="button"
            role="radio"
            aria-checked={on}
            tabIndex={o.key === focusable ? 0 : -1}
            className="chip"
            onClick={() => onChange(o.key)}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** Text input with the field chrome and an optional prefix (currency
 *  symbol). `money` keeps digits and separators only and switches to
 *  tabular figures — a paste of "¥48,000" becomes "48,000". */
export function TextField({
  id,
  value,
  onChange,
  onBlur,
  placeholder,
  prefix,
  money,
  large,
  error,
  type = 'text',
  inputMode,
  autoFocus,
  autoComplete,
  inputRef,
  describedBy,
  ariaLabel,
  spellCheck,
}: {
  id?: string;
  value: string;
  onChange: (v: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  prefix?: string;
  money?: boolean;
  large?: boolean;
  error?: boolean;
  type?: string;
  inputMode?: 'text' | 'decimal' | 'email' | 'url';
  autoFocus?: boolean;
  autoComplete?: string;
  inputRef?: Ref<HTMLInputElement>;
  describedBy?: string;
  ariaLabel?: string;
  spellCheck?: boolean;
}) {
  return (
    <div className={cx('field', error && 'is-error', large && 'is-large')}>
      {prefix && (
        <span className="field-prefix" aria-hidden="true">
          {prefix}
        </span>
      )}
      <input
        id={id}
        ref={inputRef}
        className={cx('field-input', money && 'num', large && 'is-large')}
        type={type}
        inputMode={money ? 'decimal' : inputMode}
        value={value}
        placeholder={placeholder}
        autoFocus={autoFocus}
        autoComplete={autoComplete ?? 'off'}
        spellCheck={spellCheck}
        aria-invalid={error || undefined}
        aria-describedby={describedBy}
        aria-label={ariaLabel}
        onChange={(e) => onChange(money ? e.target.value.replace(/[^0-9.,]/g, '') : e.target.value)}
        onBlur={onBlur}
      />
    </div>
  );
}

/** Multi-line text with the field chrome (feedback). Grows by dragging
 *  its corner; `rows` sets the starting height. */
export function TextArea({
  id,
  value,
  onChange,
  placeholder,
  rows = 5,
  error,
  autoFocus,
  describedBy,
  ariaLabel,
}: {
  id?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
  error?: boolean;
  autoFocus?: boolean;
  describedBy?: string;
  ariaLabel?: string;
}) {
  return (
    <textarea
      id={id}
      className={cx('field-area', error && 'is-error')}
      value={value}
      rows={rows}
      placeholder={placeholder}
      autoFocus={autoFocus}
      aria-invalid={error || undefined}
      aria-describedby={describedBy}
      aria-label={ariaLabel}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

/** Secondary explanatory text under a control. */
export function Hint({
  children,
  tone,
  id,
}: {
  children: ReactNode;
  tone?: 'owe' | 'error';
  id?: string;
}) {
  return (
    <p className={cx('hint', tone === 'owe' && 'is-owe', tone === 'error' && 'is-error')} id={id}>
      {children}
    </p>
  );
}

/** Full-width native select with the field chrome. */
export function Select({
  id,
  value,
  onChange,
  children,
  labelledBy,
}: {
  id?: string;
  value: string;
  onChange: (v: string) => void;
  children: ReactNode;
  labelledBy?: string;
}) {
  return (
    <div className="select">
      <select
        id={id}
        value={value}
        aria-labelledby={labelledBy}
        onChange={(e) => onChange(e.target.value)}
      >
        {children}
      </select>
      <Icon name="chevronDown" size={18} className="select-chevron" />
    </div>
  );
}

/** Compact pill select for the 13 supported currencies, base first —
 *  the one people pick nearly every time shouldn't need a scroll. */
export function CurrencySelect({
  value,
  onChange,
  baseCurrency,
}: {
  value: string;
  onChange: (code: string) => void;
  baseCurrency: string;
}) {
  const order = [baseCurrency, ...SUPPORTED_CURRENCIES.filter((c) => c !== baseCurrency)];
  return (
    <div className="currency-select">
      <select value={value} onChange={(e) => onChange(e.target.value)} aria-label="Currency">
        {order.map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </select>
      <Icon name="chevronDown" size={16} className="select-chevron" />
    </div>
  );
}

// ─── Date field + month grid ─────────────────────────────────────────

const MONTHS_LONG = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function isoOf(y: number, m: number, d: number): string {
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/** Sunday-first month grid (mobile MonthGrid): selected day filled with
 *  the accent, real today ringed. */
export function MonthGrid({
  valueISO,
  onPick,
}: {
  valueISO: string;
  onPick: (iso: string) => void;
}) {
  const [ym, setYm] = useState(() => ({
    y: Number(valueISO.slice(0, 4)),
    m: Number(valueISO.slice(5, 7)) - 1,
  }));
  const today = todayISO();
  const startDow = new Date(Date.UTC(ym.y, ym.m, 1)).getUTCDay();
  const days = new Date(Date.UTC(ym.y, ym.m + 1, 0)).getUTCDate();
  const cells: Array<number | null> = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= days; d++) cells.push(d);
  const step = (delta: number) =>
    setYm((s) => {
      const nm = s.m + delta;
      return { y: s.y + Math.floor(nm / 12), m: ((nm % 12) + 12) % 12 };
    });
  return (
    <div className="cal">
      <div className="cal-head">
        <button type="button" className="cal-nav" onClick={() => step(-1)} aria-label="Previous month">
          <Icon name="chevronLeft" size={20} />
        </button>
        <div className="cal-month" aria-live="polite">
          {MONTHS_LONG[ym.m]} {ym.y}
        </div>
        <button type="button" className="cal-nav" onClick={() => step(1)} aria-label="Next month">
          <Icon name="chevronRight" size={20} />
        </button>
      </div>
      <div className="cal-grid">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
          <div key={i} className="cal-dow" aria-hidden="true">
            {d}
          </div>
        ))}
        {cells.map((d, i) => {
          if (!d) return <div key={i} />;
          const iso = isoOf(ym.y, ym.m, d);
          return (
            <button
              key={i}
              type="button"
              className={cx('cal-day', iso === today && 'is-today')}
              aria-pressed={iso === valueISO}
              aria-label={mediumDate(iso)}
              onClick={() => onPick(iso)}
            >
              <span>{d}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Date row that opens the month grid below it. */
export function DateField({
  id,
  value,
  onChange,
}: {
  id?: string;
  value: string;
  onChange: (iso: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const isToday = value === todayISO();
  return (
    <>
      <button
        id={id}
        type="button"
        className="field"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <Icon name="calendar" size={20} className="field-icon" />
        <span className="field-text">
          {isToday ? `Today · ${mediumDate(value)}` : mediumDate(value)}
        </span>
        <Icon
          name="chevronRight"
          size={18}
          className="field-icon"
          style={{ transform: open ? 'rotate(90deg)' : undefined, transition: 'transform 0.15s' }}
        />
      </button>
      {open && (
        <MonthGrid
          valueISO={value}
          onPick={(iso) => {
            onChange(iso);
            setOpen(false);
          }}
        />
      )}
    </>
  );
}

// ─── Dialog ──────────────────────────────────────────────────────────

/** Modal confirm. Escape and the backdrop cancel; focus starts on the
 *  safe (cancel) button and is trapped between the two actions. */
export function ConfirmDialog({
  title,
  body,
  confirmLabel,
  cancelLabel = 'Cancel',
  destructive,
  onConfirm,
  onCancel,
}: {
  title: string;
  body?: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  destructive?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    cancelRef.current?.focus();
    return () => prev?.focus?.();
  }, []);
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onCancel();
    } else if (e.key === 'Tab') {
      e.preventDefault();
      (document.activeElement === cancelRef.current ? confirmRef : cancelRef).current?.focus();
    }
  };
  return (
    <div
      className="dialog-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        className="dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="dialog-title"
        aria-describedby={body ? 'dialog-body' : undefined}
        onKeyDown={onKeyDown}
      >
        <h2 className="dialog-title" id="dialog-title">
          {title}
        </h2>
        {body && (
          <p className="dialog-body" id="dialog-body">
            {body}
          </p>
        )}
        <div className="dialog-actions">
          <button ref={cancelRef} type="button" className="btn btn-secondary" onClick={onCancel}>
            {cancelLabel}
          </button>
          <button
            ref={confirmRef}
            type="button"
            className={cx('btn', destructive ? 'btn-danger' : 'btn-primary')}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Back-to-ledger button for error/empty screens. */
export function BackToLedger() {
  const navigate = useNavigate();
  return (
    <Button variant="secondary" onClick={() => navigate('/ledger')}>
      Back to the ledger
    </Button>
  );
}
